import type { EventInput } from '@fullcalendar/core';

import { PROJECT_STATUS_LABELS } from '@/shared/constants/statuses';
import { formatList } from '@/shared/lib/format';
import { toDateInputValue, toTimeInputValue } from '@/shared/lib/dates';
import type { CalendarItem } from '@/shared/types/domain';

/**
 * ⚠️ **A armadilha desta feature: o fuso** (spec-calendar.md §6.3, decisão 63).
 *
 * `shared/lib/dates.ts` instrui *"o FullCalendar deve ser configurado com
 * `timeZone: TIME_ZONE`"*. **Ao pé da letra isso não funciona sem dependência
 * nova**: o FullCalendar aceita nativamente só `'local'` e `'UTC'`; fuso nomeado
 * exige plugin (`@fullcalendar/luxon3` + `luxon`, ou `moment-timezone`). Seriam
 * duas bibliotecas para fazer o que o `Intl` já faz em `dates.ts` — e aquele
 * arquivo diz, duas linhas acima da instrução, que não quer biblioteca de data.
 *
 * **A saída, barata:** converter cada instante para a **hora de parede de São
 * Paulo**, sem offset (`'2026-08-14T19:00:00'`), com as funções que já existem.
 * O FullCalendar fica em `timeZone: 'local'` e renderiza a string exatamente
 * como a recebeu — em qualquer navegador, em qualquer fuso.
 *
 * Sobra **um** vazamento, e ele é fechado à mão por `nowInChurchTime()`: "hoje"
 * e o indicador de agora sairiam do relógio do navegador. Um pastor viajando não
 * pode abrir o calendário e ver o dia de hoje marcado no dia errado — é o
 * cenário que o docblock de `dates.ts` cita para existir.
 */
export function toWallClock(value: string | Date): string {
  return `${toDateInputValue(value)}T${toTimeInputValue(value)}:00`;
}

/**
 * O "agora" da igreja, para o FullCalendar destacar o dia certo.
 *
 * Sem isto, um pastor em Lisboa às 02:00 veria o calendário marcando amanhã.
 */
export function nowInChurchTime(now: Date = new Date()): string {
  return toWallClock(now);
}

/** O dia (`yyyy-mm-dd`) de um item, no fuso da igreja. */
export function dayOf(item: CalendarItem): string {
  return toDateInputValue(startOf(item));
}

export function startOf(item: CalendarItem): string {
  return item.kind === 'project'
    ? (item.project.starts_at as string)
    : item.occurrence.starts_at;
}

export function endOf(item: CalendarItem): string | null {
  return item.kind === 'project' ? item.project.ends_at : item.occurrence.ends_at;
}

export function titleOf(item: CalendarItem): string {
  if (item.kind === 'occurrence') return item.occurrence.title;
  // `title` é anulável (ADR-005): rascunho nasce vazio, e um projeto pode
  // chegar aqui sem nome se um dia a esteira aceitar isso. Nunca célula vazia.
  return item.project.title ?? 'Projeto sem título';
}

/**
 * A frase completa do item — o que o mouse parado e o leitor de tela recebem.
 *
 * O chip mostra uma linha (marcador · hora · título, decisão 57); tudo o mais
 * que a US-12 pede de cada item — ministério, espaço, situação — chega por aqui
 * e pela lista do dia, que é onde a informação cabe sem espremer a grade.
 */
export function describeItem(item: CalendarItem): string {
  if (item.kind === 'occurrence') {
    const spaces = item.occurrence.space_names;
    return [item.occurrence.title, spaces.length > 0 ? formatList(spaces) : null, 'Agenda fixa']
      .filter(Boolean)
      .join(' — ');
  }

  const { project } = item;
  const parts = [
    project.ministry_name,
    project.space_names.length > 0 ? formatList(project.space_names) : null,
    PROJECT_STATUS_LABELS[project.status],
  ].filter(Boolean);

  return `${titleOf(item)} — ${parts.join(' · ')}`;
}

/**
 * `CalendarItem` → o que o FullCalendar entende.
 *
 * `id` carrega o TIPO junto do id: `buildCalendarItems` garante um item por
 * projeto (a precedência resolve o resto), mas uma chave que só fosse o id
 * colidiria com uma ocorrência de agenda fixa que um dia compartilhasse o uuid
 * — e chave duplicada no FullCalendar some com o evento sem avisar.
 */
export function toEventInput(item: CalendarItem): EventInput {
  const end = endOf(item);

  return {
    id: `${item.kind}:${item.kind === 'project' ? item.project.id : item.occurrence.id}`,
    title: titleOf(item),
    start: toWallClock(startOf(item)),
    // Ocorrência sem fim declarado é pontual: sem `end`, o FullCalendar a
    // desenha como um instante em vez de esticá-la até o fim do dia.
    end: end ? toWallClock(end) : undefined,
    /**
     * ⚠️ **Item de mais de um dia vira "dia inteiro", e isso não é detalhe de
     * renderização — é a diferença entre a semana ser legível e não ser.**
     *
     * O FullCalendar só põe um evento na faixa de dia inteiro quando
     * `allDay: true`; um evento COM hora que dura três semanas ele espreme
     * dentro da grade de horas, e o mutirão de reforma reaparece **em todas as
     * colunas da semana às 06:00**, fingindo ser sete compromissos matinais.
     * Na faixa ele é uma barra contínua, que é o que ele é.
     *
     * É também o que o Calendário do macOS faz com o retiro que começa sexta às
     * 19h e termina domingo ao meio-dia: banner no topo, não bloco de 41 horas.
     */
    allDay: spansDays(item),
    extendedProps: { item },
  };
}

/** O item atravessa a virada do dia, no fuso da igreja. */
export function spansDays(item: CalendarItem): boolean {
  const end = endOf(item);
  return Boolean(end && toDateInputValue(end) !== dayOf(item));
}

/**
 * O dia civil de uma `Date` **do FullCalendar** — lido dos campos LOCAIS, nunca
 * convertido de fuso.
 *
 * ⚠️ Passar essas datas por `toDateInputValue` seria o erro sutil desta feature.
 * O adaptador alimenta o FullCalendar com hora de parede da igreja e o mantém em
 * `timeZone: 'local'`; as datas que ele devolve (`dateClick`, `datesSet`) já
 * SÃO a hora de parede, com os campos locais certos e o instante absoluto
 * errado de propósito. Reconvertê-las para São Paulo aplicaria o deslocamento
 * duas vezes — e num navegador a leste de Brasília o mês visível viraria o
 * anterior no dia 1º.
 */
export function localDayOf(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** O dia de hoje no fuso da igreja, `yyyy-mm-dd`. */
export function churchToday(now: Date = new Date()): string {
  return toDateInputValue(now);
}
